import { Form, Formik } from 'formik'
import * as React from 'react'
import * as mui from '@mui/material'
import * as Sentry from '@sentry/react'

import { CenteredSpinner } from 'components/CenteredSpinner'
import { LoadFailure } from 'components/LoadFailure'
import { translationsContext } from 'context/translations'
import { userContext } from 'context/user'
import * as schema from 'types/graphql/schema'
import { graphqlClient, requireErrorExtensions, useQuery } from 'utils/graphql'
import { convertCurrencyInputs } from 'utils/units'
import { renderValues, validationSchema, valuesToCalcInput, valuesType } from 'utils/values'

import { Main } from './Main'

import logoAdisseo from './assets/adisseo.png'
import devenishLogo from './assets/devenish.jpg'


export function Index() {
  const t = React.useContext(translationsContext)
  const user = React.useContext(userContext)

  const [calculationResult, setCalculationResult] = React.useState<schema.Calculate>()
  const [commonErrors, setCommonErrors] = React.useState<string[]>([])
  const [initialValues, setInitialValues] = React.useState<valuesType>()
  const [investmentColumnsStatuses, setInvestmentColumnsStatuses] = React.useState([
    schema.ColumnStatus.OFF,
    schema.ColumnStatus.ON,
    schema.ColumnStatus.ON,
    schema.ColumnStatus.ON,
    schema.ColumnStatus.ON
  ])
  const [investmentColumnsStatusesInitialised, setInvestmentColumnsStatusesInitialised] = React.useState(false)
  const [productMetrics, setProductMetrics] = React.useState<schema.ProductMetrics>()

  const [selectedDate, setSelectedDate] = React.useState<Date|null>(new Date())
  const [farm, setFarm] = React.useState('')
  const [nutritionist, setNutritionist] = React.useState('')
  const [notes, setNotes] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [isEmail, setIsEmail] = React.useState(false)

  const notesState = {
    selectedDate,
    setSelectedDate,
    farm,
    setFarm,
    nutritionist,
    setNutritionist,
    notes,
    setNotes,
    isEmail,
    setIsEmail,
    email,
    setEmail,
  }

  const pdfRef = React.useRef<HTMLDivElement>(null)

  const constants = useQuery({
    currencies: {__scalar: 1},
    products: {
      __scalar: 1,
      metrics: {
        standards: {id: 1},
        diseasePerformances: {__scalar: 1},
        economicPerformance: {__scalar: 1},
        milkPerformance: {__scalar: 1},
        milkPerformanceDiff: {__scalar: 1},
        productFeedingRates: {__scalar: 1},
        reproductivePerformance: {__scalar: 1},
      },
    },
    standards: {
      __scalar: 1,
      region: {
        __scalar: 1,
        countries: {__scalar: 1},
        currency: {__scalar: 1},
      },
      currentFeedCosts: {__scalar: 1},
      diseaseMetrics: {__scalar: 1},
      fmmoPricing: {__scalar: 1},
      dmi: {__scalar: 1},
      herdComposition: {__scalar: 1},
    }
  })

  const standards = getStandards()
  let currency = standards?.region.currency
  if (user.data!.defaultCalculationTemplate) {
    currency = user.data!.defaultCalculationTemplate.calculationInput.currency
  } else if (constants.data) {
    if (user.data?.country === schema.MainUserCountryChoices.JP) {
      currency = constants.data.currencies.find(c => c.code === 'JPY') || currency
    } else if (user.data?.country === schema.MainUserCountryChoices.BR) {
      currency = constants.data.currencies.find(c => c.code === 'BRL') || currency
    } else if (user.data?.country === schema.MainUserCountryChoices.GB) {
      currency = constants.data.currencies.find(c => c.code === 'GBP') || currency
    }
  }

  React.useEffect(() => {
    if (!user.data || !constants.data || !currency) return

    const product = user.data!.defaultCalculationTemplate
      ? constants.data.products.find(p => p.id === user.data!.defaultCalculationTemplate!.calculationInput.product.id) || constants.data.products[0]
      : constants.data.products[0]

    const standards = getStandards()!

    const productMetrics = product.metrics
      .find(m => m.standards.id === standards.id) as schema.ProductMetrics

    setProductMetrics(productMetrics)

    const diseasePerformance = productMetrics.diseasePerformances.reduce((acc, d) => {
      acc[d.disease] = d
      return acc
    }, {} as {[d in schema.DiseasePerformanceDisease]: schema.DiseasePerformance })

    const values = renderValues(
      user.data!.defaultCalculationTemplate
        ? {
          ...(() => {
            // Removing the object fields received from API
            const { currency, id, region, product, ...rest } = user.data!.defaultCalculationTemplate.calculationInput
            return rest
          })(),
          investmentFarOffStatus: user.data!.defaultCalculationTemplate.calculationInput.investmentFarOffStatus as unknown as schema.ColumnStatus,
          investmentCloseUpStatus: user.data!.defaultCalculationTemplate.calculationInput.investmentCloseUpStatus as unknown as schema.ColumnStatus,
          investmentFreshStatus: user.data!.defaultCalculationTemplate.calculationInput.investmentFreshStatus as unknown as schema.ColumnStatus,
          investmentEarlyMidLactStatus: user.data!.defaultCalculationTemplate.calculationInput.investmentEarlyMidLactStatus as unknown as schema.ColumnStatus,
          investmentLateLactStatus: user.data!.defaultCalculationTemplate.calculationInput.investmentLateLactStatus as unknown as schema.ColumnStatus,
          currencyId: user.data!.defaultCalculationTemplate.calculationInput.currency.id,
          regionId: user.data!.defaultCalculationTemplate.calculationInput.region.id,
          resultsUnitsAmount: user.data!.defaultCalculationTemplate.calculationInput.resultsUnitsAmount as unknown as schema.ResultsUnitsAmount,
          resultsUnitsPeriod: user.data!.defaultCalculationTemplate.calculationInput.resultsUnitsPeriod as unknown as schema.ResultsUnitsPeriod,
          productId: product.id,
          productPrice: user.data!.defaultCalculationTemplate.calculationInput.productPrice,
          units: user.data!.defaultCalculationTemplate.calculationInput.units as unknown as schema.Units,
        } as schema.CalculationInput
        : {
          units: user.data!.clientCustomization === schema.UserClientCustomizations.DEVENISH
            ? schema.Units.METRIC
            : standards.region.units as string,
          resultsUnitsAmount: schema.ResultsUnitsAmount.COW,
          resultsUnitsPeriod: schema.ResultsUnitsPeriod.YEAR,
          currencyId: currency.id,
          herdSize: 1000,
          aabCents: 0,
          isPlusDmi: false,
          isAab: true,
          productId: product.id,
          productPrice: product.price,
          regionId: standards.region.id,

          investmentHerdStructureFarOff: standards.herdComposition.farOff,
          investmentHerdStructureCloseUp: standards.herdComposition.closeUp,
          investmentHerdStructureFresh: standards.herdComposition.fresh,
          investmentHerdStructureEarlyMidLact: standards.herdComposition.earlyMidLact,
          investmentHerdStructureLateLact: standards.herdComposition.lateLact,

          investmentProductGramsPerCowDayCloseUp: productMetrics.productFeedingRates!.closeUp,
          investmentProductGramsPerCowDayFresh: productMetrics.productFeedingRates!.fresh,
          investmentProductGramsPerCowDayEarlyMidLact: productMetrics.productFeedingRates!.earlyMidLact,
          investmentProductGramsPerCowDayLateLact: productMetrics.productFeedingRates!.lateLact,

          investmentDmiFarOff: standards.dmi.farOff,
          investmentDmiCloseUp: standards.dmi.closeUp,
          investmentDmiFresh: standards.dmi.fresh,
          investmentDmiEarlyMidLact: standards.dmi.earlyMidLact,
          investmentDmiLateLact: standards.dmi.lateLact,

          investmentDmCostCurrentFarOff: standards.currentFeedCosts.farOff,
          investmentDmCostCurrentCloseUp: standards.currentFeedCosts.closeUp,
          investmentDmCostCurrentFresh: standards.currentFeedCosts.fresh,
          investmentDmCostCurrentEarlyMidLact: standards.currentFeedCosts.earlyMidLact,
          investmentDmCostCurrentLateLact: standards.currentFeedCosts.lateLact,


          shortTermReturnsMilkCurrent: productMetrics.milkPerformance.milk,
          shortTermReturnsMilkDifference: productMetrics.milkPerformanceDiff.milk,

          shortTermReturnsFatCurrent: productMetrics.milkPerformance.fat,
          shortTermReturnsFatDifference: productMetrics.milkPerformanceDiff.fat,

          shortTermReturnsProteinCurrent: productMetrics.milkPerformance.protein,
          shortTermReturnsProteinDifference: productMetrics.milkPerformanceDiff.protein,

          shortTermReturnsOtherSolidsCurrent: productMetrics.milkPerformance.otherSolids,
          shortTermReturnsOtherSolidsDifference: productMetrics.milkPerformanceDiff.otherSolids,

          shortTermReturnsMilkFmmoPrice: standards.fmmoPricing.milk,
          shortTermReturnsFatFmmoPrice: standards.fmmoPricing.fat,
          shortTermReturnsProteinFmmoPrice: standards.fmmoPricing.protein,
          shortTermReturnsOtherSolidsFmmoPrice: standards.fmmoPricing.otherSolids,
          shortTermReturnsPremiumFmmoPrice: standards.fmmoPricing.premium,

          longTermReturnsReproductionCostCurrent: productMetrics.economicPerformance.reproductionCost,
          longTermReturnsReplacementCostCurrent: productMetrics.economicPerformance.replacementCost,
          longTermReturnsCullValueCurrent: productMetrics.economicPerformance.cullValue,
          longTermReturnsCalfValueCurrent: productMetrics.economicPerformance.calfValue,
          longTermReturnsCullingRateCurrent: productMetrics.economicPerformance.cullingRate,

          longTermReturnsLastMonthToBreedCurrent: productMetrics.reproductivePerformance.lastMonthToBreed,
          longTermReturnsMinimumMilkToBreedCurrent: productMetrics.reproductivePerformance.minimumMilkToBreed,
          longTermReturnsPregnancyRateCurrent: productMetrics.reproductivePerformance.pregnancyRate,
          longTermReturnsPregnancyRateDifference: productMetrics.reproductivePerformance.pregnancyRateDiff,
          longTermReturnsPregnancyLossCurrent: productMetrics.reproductivePerformance.pregnancyLoss,
          longTermReturnsPregnancyLossDifference: productMetrics.reproductivePerformance.pregnancyLossDiff,

          longTermReturnsMastitisRegular: diseasePerformance.MASTITIS_CLINICAL.regularIncidence,
          longTermReturnsMastitisReduced: diseasePerformance.MASTITIS_CLINICAL.reducedIncidence,
          longTermReturnsMastitisCostPerCase: standards.diseaseMetrics.find(d => d.disease === schema.DiseaseMetricsDisease.MASTITIS_CLINICAL)!.usdCase,

          longTermReturnsKetosisClinicalRegular: diseasePerformance.KETOSIS_CLINICAL.regularIncidence,
          longTermReturnsKetosisClinicalReduced: diseasePerformance.KETOSIS_CLINICAL.reducedIncidence,
          longTermReturnsKetosisClinicalCostPerCase: standards.diseaseMetrics.find(d => d.disease === schema.DiseaseMetricsDisease.KETOSIS_CLINICAL)!.usdCase,

          longTermReturnsKetosisSubClinicalRegular: diseasePerformance.KETOSIS_SUBCLINICAL.regularIncidence,
          longTermReturnsKetosisSubClinicalReduced: diseasePerformance.KETOSIS_SUBCLINICAL.reducedIncidence,
          longTermReturnsKetosisSubclinicalCostPerCase: standards.diseaseMetrics.find(d => d.disease === schema.DiseaseMetricsDisease.KETOSIS_SUBCLINICAL)!.usdCase,

          longTermReturnsMetritisRegular: diseasePerformance.METRITIS.regularIncidence,
          longTermReturnsMetritisReduced: diseasePerformance.METRITIS.reducedIncidence,
          longTermReturnsMetritisCostPerCase: standards.diseaseMetrics.find(d => d.disease === schema.DiseaseMetricsDisease.METRITIS)!.usdCase,
        } as schema.CalculationInput,
      currency.code
    )

    if (user.data!.defaultCalculationTemplate) {
      setInvestmentColumnsStatuses([
        user.data!.defaultCalculationTemplate.calculationInput.investmentFarOffStatus as unknown as schema.ColumnStatus,
        user.data!.defaultCalculationTemplate.calculationInput.investmentCloseUpStatus as unknown as schema.ColumnStatus,
        user.data!.defaultCalculationTemplate.calculationInput.investmentFreshStatus as unknown as schema.ColumnStatus,
        user.data!.defaultCalculationTemplate.calculationInput.investmentEarlyMidLactStatus as unknown as schema.ColumnStatus,
        user.data!.defaultCalculationTemplate.calculationInput.investmentLateLactStatus as unknown as schema.ColumnStatus,
      ])
      setInvestmentColumnsStatusesInitialised(true)
    }
    const convertValues = async () => {
      if (user.data!.defaultCalculationTemplate) {
        const convertedValues = renderValues(values, currency!.code)
        await setInitialValues(convertedValues)
        return convertedValues
      }
      return await convertCurrencyInputs(
        constants.data.products,
        (v: valuesType) => setInitialValues(v as any),
        1,
        currency!,
        values,
      )
    }
    convertValues().then((v) => calculate(v))
  }, [constants.data, user.data])

  function calculate(values: valuesType) {
    setCommonErrors([])
    graphqlClient.chain.mutation
      .calculate({input: {
          ...valuesToCalcInput(
            values, !user.data?.defaultCalculationTemplate || investmentColumnsStatusesInitialised
            ? investmentColumnsStatuses
            : [
                user.data?.defaultCalculationTemplate.calculationInput.investmentFarOffStatus as unknown as schema.ColumnStatus,
                user.data?.defaultCalculationTemplate.calculationInput.investmentCloseUpStatus as unknown as schema.ColumnStatus,
                user.data?.defaultCalculationTemplate.calculationInput.investmentFreshStatus as unknown as schema.ColumnStatus,
                user.data?.defaultCalculationTemplate.calculationInput.investmentEarlyMidLactStatus as unknown as schema.ColumnStatus,
                user.data?.defaultCalculationTemplate.calculationInput.investmentLateLactStatus as unknown as schema.ColumnStatus,
              ]
          ),
        }})
      .execute({
        __scalar: 1,
        currency: {__scalar: 1},
        investment: {
          __scalar: 1,
          cowsAmount: {__scalar: 1},
          productPricePerCowDay: {__scalar: 1},
          dmiWithProduct: {__scalar: 1},
          dmCostWithProduct: {__scalar: 1},
          feedCostPerCowDay: {__scalar: 1},
          feedCostPerCowDayWithProduct: {__scalar: 1},
        },
        product: {__scalar: 1},
        shortTermReturns: {
          __scalar: 1,
          fatPerCowDay: {__scalar: 1},
          proteinPerCowDay: {__scalar: 1},
          otherSolidsPerCowDay: {__scalar: 1},
          ecmPerCowDay: {__scalar: 1},
          impactCwtCost: {__scalar: 1},
          impactPerCowDayCost: {__scalar: 1},
          impactPerCowYearCost: {__scalar: 1},
          totalImpactCost: {__scalar: 1},
        },
        longTermReturnsReproduction: {
          __scalar: 1,
          calfSales: {__scalar: 1},
          mortalityCost: {__scalar: 1},
          reproductiveCost: {__scalar: 1},
          reproductiveCullingCost: {__scalar: 1},
          nonReproductiveCullingCost: {__scalar: 1},
          totalReturns: {__scalar: 1},
          totalReturnsFinal: {__scalar: 1},
        },
        longTermReturnsHealth: {
          mastitis: {__scalar: 1},
          ketosisClinical: {__scalar: 1},
          ketosisSubclinical: {__scalar: 1},
          metritis: {__scalar: 1},
          totalSavings: {__scalar: 1},
          totalSavingsFinal: {__scalar: 1},
        },
      })
      .then(r => setCalculationResult(r!))
      .catch(e => {
        const {common_errors} = requireErrorExtensions(e)
        // This page renders no field errors, so a GraphQL error without common_errors
        // is nothing we can show the user — let it reach Sentry.
        if (!common_errors) throw e
        setCommonErrors(common_errors)
      })
  }

  function getStandards() {
    if (!constants.data?.standards) return

    if (user.data?.defaultCalculationTemplate) {
      return constants.data.standards.find(s => s.region.id === user.data?.defaultCalculationTemplate!.calculationInput.region.id)
    }
    return (
      constants.data.standards.find(s => !!s.region.countries.find(c => c.code === user.data?.country))
      || constants.data.standards.find(s => s.region.name === 'Other')
      || constants.data.standards[0]
    )
  }

  if (constants.failure) return <LoadFailure onRetry={() => constants.refetch()} />
  if (!calculationResult || constants.spinner || !productMetrics) return <CenteredSpinner />

  return (
    <mui.Box
      sx={{
        border: '7px solid #A70A2D',
        minHeight: '100dvh',
        minWidth: 'calc(1530px + 14px)',
        pb: '14px',
      }}
    >
      <Formik<valuesType>
        initialValues={initialValues as valuesType}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          // calculate(values as unknown as CalculationInput)
          setSubmitting(false)
        }}
      >
        {({submitForm, values}) => {
          React.useEffect(() => {
            if (investmentColumnsStatusesInitialised) {
              submit(values)
            } else {
              setInvestmentColumnsStatusesInitialised(true)
            }
          }, [investmentColumnsStatuses])

          React.useEffect(() => {
            Sentry.setContext('calculatorValues', values as Record<string, unknown>)
            Sentry.setContext('investmentColumnsStatuses', {statuses: investmentColumnsStatuses})
          }, [values, investmentColumnsStatuses])

          function submit(newValues: valuesType) {
            submitForm().then()
            try {
              validationSchema.validateSync(newValues, {abortEarly: false})
            } catch (e) {
              console.warn('VALIDATION FAILED', (e as any).errors)
              return
            }
            console.log('VALIDATION OK')
            calculate(newValues)
          }
          return (
            <Form>
              <mui.Grid
                item
                xs={12}
                sx={{
                  backgroundColor: '#A70A2D',
                  borderBottom: '10px solid #B6C1C6',
                  color: 'white',
                  mr: '14px',
                  mt: '-7px',
                  py: 1,
                  position: 'sticky',
                  top: 0,
                  width: '100%',
                  minWidth: '1530px',
                  zIndex: 1000,
                }}
              >
                <mui.Stack direction="row" sx={{justifyContent: 'space-between', pr: '300px'}}>
                  <mui.Stack direction="row" spacing={2}>
                    <mui.Button
                      href="mailto:fernanda.lopes@adisseo.com,lucas.rebelo@adisseo.com,douglas.cornwell@adisseo.com"
                      sx={{color: '#B6C1C6', fontSize: '1.1rem'}}
                      variant="text"
                    >
                      {t('calc__header__button__help')}
                    </mui.Button>
                    <mui.Button
                      onClick={() => graphqlClient.chain.mutation
                        .logout
                        .execute({__scalar: 1})
                        .then(() => window.location.reload())
                      }
                      sx={{color: '#B6C1C6', fontSize: '1.1rem'}}
                      variant="text"
                    >
                      {t('calc__header__button__logout')}
                    </mui.Button>
                  </mui.Stack>

                  <mui.Typography
                    variant="h1"
                    sx={{fontFamily: 'Exo, Roboto, Helvetica, Arial, sans-serif'}}
                  >
                    {t('calc__header__title')}
                  </mui.Typography>
                  <div></div>
                </mui.Stack>

                <mui.Box sx={{position: 'absolute', right: 0, bottom: '-29px'}}>
                  <img src={logoAdisseo} alt="Adisseo" style={{marginBottom: '5px'}} height="90"/>
                </mui.Box>

                {user.data!.clientCustomization === schema.UserClientCustomizations.DEVENISH &&
                  <mui.Box sx={{position: 'absolute', right: 37, bottom: '-75px'}}>
                    <img src={devenishLogo} alt="devenish logo" height="30"/>
                  </mui.Box>
                }
              </mui.Grid>
              <Main
                calculate={submit}
                calculationResult={calculationResult}
                commonErrors={commonErrors}
                constants={constants}
                standards={standards!}
                initialCurrency={currency!}
                investmentColumnsStatuses={investmentColumnsStatuses}
                productMetrics={productMetrics!}
                setInvestmentColumnsStatuses={setInvestmentColumnsStatuses}
                notesState={notesState}
                pdfRef={pdfRef}
              />
              <mui.Box sx={{position: 'fixed', left: 10000}}>
                <mui.Box ref={pdfRef}>
                  <mui.Grid
                    item
                    xs={12}
                    sx={{
                      backgroundColor: '#A70A2D',
                      borderBottom: '10px solid #B6C1C6',
                      color: 'white',
                      mb: 3,
                      position: 'relative',
                      py: 1,
                    }}
                  >
                    <mui.Typography
                      variant="h2"
                      sx={{
                        fontFamily: 'Exo, Roboto, Helvetica, Arial, sans-serif',
                        textAlign: 'center',
                        ml: -30,
                      }}
                    >
                      {t('calc__pdf__header__title')}
                    </mui.Typography>

                    <mui.Box sx={{position: 'absolute', right: 0, bottom: '-29px'}}>
                      <img src={logoAdisseo} alt="Adisseo" style={{marginBottom: '5px'}} height="90"/>
                    </mui.Box>
                  </mui.Grid>
                  <Main
                    calculate={submit}
                    calculationResult={calculationResult}
                    commonErrors={commonErrors}
                    constants={constants}
                    initialCurrency={currency!}
                    investmentColumnsStatuses={investmentColumnsStatuses}
                    setInvestmentColumnsStatuses={setInvestmentColumnsStatuses}
                    notesState={notesState}
                    productMetrics={productMetrics!}
                    standards={standards!}
                  />
                </mui.Box>
              </mui.Box>
            </Form>
          )}}
      </Formik>
    </mui.Box>
  )
}
